import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Plus, Trash2, X } from 'lucide-react';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import {
  TYPES, newQuestion, newOption, rowProblems, slugify,
} from './questions';

/**
 * QuestionsEditor — the typed questions a decision model answers, one card
 * each: a name, the type, the question, then what that type needs.
 *   Yes / no  optional "when yes" / "when no", and the yes threshold
 *   Choice    named options, each with a description
 *   Score     2 to 10 ordered levels, lowest first, each described
 *
 * Controlled: `rows` in (see questions.js), `onChange(rows)` out.
 */

export const fieldClass = 'w-full rounded-md border border-[color:var(--border-default)] bg-surface px-2.5 text-[12px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-disabled)] transition-[border-color] duration-150 focus:border-[color:var(--border-strong)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]';
const inputClass = `h-8 ${fieldClass}`;
const areaClass = `min-h-[56px] resize-y py-1.5 leading-5 ${fieldClass}`;
const labelClass = 'mb-1 block text-[11px] font-medium text-[color:var(--text-muted)]';
const iconButton = 'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)] disabled:opacity-40';
export const addButtonClass = 'inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--border-default)] bg-surface px-2.5 text-[12px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)] disabled:opacity-50';
const quietButton = 'inline-flex items-center gap-1 text-[12px] font-medium text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)] disabled:opacity-40';

function NoulFields({ q, set, readOnly }) {
  return (
    <>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <label className="block">
          <span className={labelClass}>When yes</span>
          <input className={inputClass} value={q.yes} disabled={readOnly} placeholder="Optional" onChange={(e) => set({ yes: e.target.value })} />
        </label>
        <label className="block">
          <span className={labelClass}>When no</span>
          <input className={inputClass} value={q.no} disabled={readOnly} placeholder="Optional" onChange={(e) => set({ no: e.target.value })} />
        </label>
      </div>
      <label className="flex items-center gap-2 text-[12px] text-[color:var(--text-secondary)]">
        <span>Yes at or above</span>
        <input
          type="number" min={0.05} max={0.95} step={0.05}
          className={`h-7 w-[72px] ${fieldClass}`}
          value={q.threshold}
          disabled={readOnly}
          aria-label="Yes threshold"
          onChange={(e) => {
            const v = Number(e.target.value);
            if (Number.isFinite(v)) set({ threshold: Math.min(0.95, Math.max(0.05, v)) });
          }}
        />
      </label>
    </>
  );
}

function ChoiceFields({ q, set, readOnly }) {
  const options = q.options || [];
  const setOption = (i, patch) => set({ options: options.map((o, j) => (j === i ? { ...o, ...patch } : o)) });
  return (
    <div>
      <span className={labelClass}>Options</span>
      <div className="space-y-1.5">
        {options.map((o, i) => (
          <div key={o.uid} className="flex items-center gap-1.5">
            <input
              className={`${inputClass} max-w-[180px] font-mono`}
              value={o.key}
              disabled={readOnly}
              aria-label={`Option ${i + 1} name`}
              placeholder="option_name"
              onChange={(e) => setOption(i, { key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
            />
            <input
              className={inputClass}
              value={o.description}
              disabled={readOnly}
              aria-label={`Option ${i + 1} description`}
              placeholder="Description"
              onChange={(e) => setOption(i, { description: e.target.value })}
            />
            {!readOnly && (
              <button type="button" className={iconButton} aria-label={`Remove option ${o.key}`} disabled={options.length <= 2} onClick={() => set({ options: options.filter((_, j) => j !== i) })}>
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>
      {!readOnly && (
        <button type="button" className={`${quietButton} mt-2`} disabled={options.length >= 255} onClick={() => set({ options: [...options, newOption(options.map((o) => o.key))] })}>
          <Plus className="h-3.5 w-3.5" /> Add option
        </button>
      )}
    </div>
  );
}

function ScoreFields({ q, set, readOnly }) {
  const levels = q.levels || [];
  return (
    <div>
      <span className={labelClass}>Levels (lowest first)</span>
      <div className="space-y-1.5">
        {levels.map((l, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className="flex items-center gap-1.5">
            <span className="w-14 shrink-0 text-[12px] tabular-nums text-[color:var(--text-muted)]">Level {i}</span>
            <input
              className={inputClass}
              value={l}
              disabled={readOnly}
              aria-label={`Level ${i}`}
              placeholder="Description"
              onChange={(e) => set({ levels: levels.map((x, j) => (j === i ? e.target.value : x)) })}
            />
            {!readOnly && (
              <button type="button" className={iconButton} aria-label={`Remove level ${i}`} disabled={levels.length <= 2} onClick={() => set({ levels: levels.filter((_, j) => j !== i) })}>
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>
      {!readOnly && (
        <button type="button" className={`${quietButton} mt-2`} disabled={levels.length >= 10} onClick={() => set({ levels: [...levels, ''] })}>
          <Plus className="h-3.5 w-3.5" /> Add level
        </button>
      )}
    </div>
  );
}

function QuestionCard({ q, problems, onChange, onRemove, readOnly, showProblems }) {
  const [open, setOpen] = useState(true);
  const set = (patch) => onChange({ ...q, ...patch });
  const changeType = (type) => {
    if (type === q.type) return;
    const fresh = newQuestion(type, []);
    onChange({ ...fresh, uid: q.uid, key: q.key, instructions: q.instructions });
  };
  return (
    <div className="rounded-lg border border-[color:var(--border-default)] bg-surface" data-testid="question-card">
      <div className="flex items-center gap-1.5 px-2 py-1.5">
        <button type="button" className={iconButton} aria-label={open ? 'Collapse question' : 'Expand question'} aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
        <input
          className="h-7 min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-1.5 font-mono text-[12px] font-medium text-[color:var(--text-primary)] hover:border-[color:var(--border-default)] focus:border-[color:var(--border-strong)] focus:outline-none"
          value={q.key}
          disabled={readOnly}
          aria-label="Question name"
          onChange={(e) => set({ key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_').slice(0, 40) })}
        />
        <div className="w-[112px] shrink-0">
          <BrandDropdown
            value={q.type}
            options={TYPES}
            disabled={readOnly}
            ariaLabel="Question type"
            compact
            triggerClassName="!h-7 !px-2 !py-0 !bg-transparent !text-[12px]"
            onChange={(v) => changeType(v)}
          />
        </div>
        {!readOnly && (
          <button type="button" className={iconButton} aria-label={`Delete ${q.key}`} onClick={onRemove}>
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {open && (
        <div className="space-y-3 border-t border-[color:var(--border-default)] px-3 pb-3 pt-2.5">
          <textarea
            className={areaClass}
            value={q.instructions}
            disabled={readOnly}
            aria-label="Question"
            placeholder="Question"
            onChange={(e) => {
              const instructions = e.target.value;
              // A name still at its default follows the question as it is typed.
              const auto = /^(yes_no|choice|score)(_\d+)?$/.test(q.key) && instructions.trim().length > 3;
              set(auto ? { instructions, key: slugify(instructions.split(/[?.!]/)[0].split(' ').slice(0, 5).join(' '), q.key) } : { instructions });
            }}
          />
          {q.type === 'noul' && <NoulFields q={q} set={set} readOnly={readOnly} />}
          {q.type === 'choice' && <ChoiceFields q={q} set={set} readOnly={readOnly} />}
          {q.type === 'score' && <ScoreFields q={q} set={set} readOnly={readOnly} />}
        </div>
      )}
      {showProblems && problems && (
        <ul className="border-t border-[color:var(--border-default)] px-3 py-2 text-[12px] text-[color:var(--feedback-error)]">
          {problems.map((p) => <li key={p}>{p}</li>)}
        </ul>
      )}
    </div>
  );
}

export function AddQuestionButtons({ rows, onChange, disabled }) {
  const taken = rows.map((r) => r.key);
  return (
    <div className="flex flex-wrap gap-1.5">
      {TYPES.map((t) => (
        <button key={t.value} type="button" className={addButtonClass} disabled={disabled || rows.length >= 50} onClick={() => onChange([...rows, newQuestion(t.value, taken)])}>
          <Plus className="h-3.5 w-3.5" /> {t.label}
        </button>
      ))}
    </div>
  );
}

export default function QuestionsEditor({ rows, onChange, readOnly = false, showProblems = false }) {
  const problems = rowProblems(rows);
  return (
    <div className="space-y-2">
      {rows.map((q, i) => (
        <QuestionCard
          key={q.uid}
          q={q}
          readOnly={readOnly}
          problems={problems[q.uid]}
          showProblems={showProblems}
          onChange={(next) => onChange(rows.map((r, j) => (j === i ? next : r)))}
          onRemove={() => onChange(rows.filter((_, j) => j !== i))}
        />
      ))}
      {!readOnly && <AddQuestionButtons rows={rows} onChange={onChange} />}
    </div>
  );
}
